<script setup lang="ts">
import { useIsFormValid } from 'vee-validate'
import type { BaseFormComponentsBaseCalendar } from '#build/components'
import type { HouseDetails } from '~/types/house/types'
import type { PeriodDurationInquiry } from '~/types/inquiry/types'

defineProps<{
  bookedDates: string[][]
  bookedDatesExceptChecks: string[]
  house: HouseDetails
  periods: PeriodDurationInquiry[]
}>()
const emits = defineEmits<{
  'next-months': [Date, Date]
  'period-selected': [{ startDate: Date; endDate: Date } | null]
}>()
const { isMobile } = useBreakpoint()
const isFormValid = useIsFormValid()

const formValues = defineModel<{
  email: string
  endDate: Date | null
  firstName: string
  lastName: string
  message: string
  phone: string
  startDate: Date | null
}>({ default: () => ({}) })

const latestStartDate = ref(formValues.value.startDate ?? undefined)
const calendar = ref<InstanceType<
  typeof BaseFormComponentsBaseCalendar
> | null>(null)

const activeIndex = computed(() => calendar.value?.activeIndex)
const isCalendarOpen = computed(() => Boolean(calendar.value?.showCalendar))

const handleChangeDates = async (
  date: Date,
  field: 'endDate' | 'startDate',
) => {
  formValues.value[field] = date
  if (field === 'endDate' && date && formValues.value.startDate) {
    emits('period-selected', {
      endDate: date,
      startDate: formValues.value.startDate,
    })
  } else if (field === 'startDate') {
    emits('period-selected', null)
  }
}

watch(activeIndex, (newValue, oldValue) => {
  if (typeof newValue === 'number' && typeof oldValue === 'number') {
    const diff = newValue - oldValue
    const startDate = $dayjs(latestStartDate.value)
      .add(diff, 'month')
      .startOf('month')
    const endDate = startDate.add(isMobile.value ? 7 : 2, 'month').toDate()
    latestStartDate.value = startDate.toDate()
    emits('next-months', latestStartDate.value, endDate)
  }
})

defineExpose({ isCalendarOpen, isFormValid })
</script>

<template>
  <template v-if="!isCalendarOpen">
    <p>{{ $t('inquiry.sendAMessage.subtitle') }}</p>
    <div class="grid grid-cols-1 gap-x-4 sm:grid-cols-2">
      <BaseFormComponentsBaseInput
        v-model="formValues.firstName"
        name="firstName"
        rules="required"
        :label="`${$t('field.firstName')}`"
      />
      <BaseFormComponentsBaseInput
        v-model="formValues.lastName"
        name="lastName"
        rules="required"
        :label="`${$t('field.lastName')}`"
      />
      <BaseFormComponentsBaseInput
        v-model="formValues.email"
        name="email"
        rules="email|required"
        :label="`${$t('field.email')}`"
      />
      <BaseFormComponentsBaseTelInput
        v-model="formValues.phone"
        name="phone"
        rules="required"
        :label="`${$t('field.phoneSingle')}`"
      />
    </div>
  </template>

  <BaseFormComponentsBaseCalendar
    ref="calendar"
    name="inquiry-dates"
    disabled-days-before-day-date
    validate-on-empty
    validate-on-mount
    :class="isCalendarOpen ? 'hide-input' : ''"
    :booked-dates="bookedDates.flat()"
    :end-date="formValues.endDate"
    :has-footer="!isMobile"
    :has-header="isMobile"
    :is-affixed="isMobile"
    :label="isCalendarOpen ? '' : `${$t('field.dates')}`"
    :period-dates="periods"
    :placeholder="{
      checkIn: $t('placeholders.calendarCheckIn'),
      checkOut: $t('placeholders.calendarCheckOut'),
    }"
    :rules="`calendarRequired|datesAlreadyBooked:${bookedDatesExceptChecks}`"
    :start-date="formValues.startDate"
    @update:end-date="handleChangeDates($event, 'endDate')"
    @update:start-date="handleChangeDates($event, 'startDate')"
  >
    <template
      v-if="!isMobile"
      #lc-calendar-footer="{ clearDates, closeDatePicker }"
    >
      <div class="flex items-center justify-end gap-8 pr-2 text-md font-medium">
        <BaseButton
          variant="link"
          class="underline"
          color="black"
          :disabled="!formValues.startDate && !formValues.endDate"
          @click="clearDates"
        >
          {{ $t('inquiry.clearDates') }}
        </BaseButton>
        <BaseButton
          variant="link"
          class="underline"
          color="black"
          @click="closeDatePicker"
        >
          {{ $t('global.close') }}
        </BaseButton>
      </div>
    </template>
    <template
      v-else
      #lc-calendar-header-mobile="{ clearDates, closeDatePicker }"
    >
      <div class="mb-4 flex w-full items-center justify-between">
        <BaseButton
          variant="link"
          class="!text-md !font-medium"
          color="black"
          has-icon
          @click="closeDatePicker"
        >
          <BaseIcon name="chevronLeft" :size="1.5" />
          {{ $t('action.back') }}
        </BaseButton>
        <BaseButton
          variant="link"
          class="!text-md underline"
          color="black"
          :disabled="!formValues.startDate && !formValues.endDate"
          @click="clearDates"
        >
          {{ $t('inquiry.clearDates') }}
        </BaseButton>
      </div>
    </template>
  </BaseFormComponentsBaseCalendar>

  <BaseFormComponentsBaseTextarea
    v-if="!isCalendarOpen"
    v-model="formValues.message"
    wrapper-class="col-span-2 mb-4"
    name="message"
    rules="required"
    :rows="isMobile ? 5 : 3"
    :label="`${$t('field.yourMessage.label')}`"
    :placeholder="$t('field.yourMessage.placeholder')"
  />
</template>

<style>
.vue-calendar#inquiry-dates.hide-input .calendar_input {
  @apply !hidden;
}
.vue-calendar#inquiry-dates .calendar_wrapper {
  @apply !shadow-none !pl-0;
}
.vue-calendar#inquiry-dates.base-calendar--hasError:has(.calendar_input-open) {
  @apply !border-none;
}
</style>
